import React, { useState } from 'react'
import { isEmpty } from 'lodash'
import { IconCheck, IconPencil } from '@tabler/icons-react'

import { Button } from '@/components/buttons/Button'
import { TextArea } from '@/components/forms/TextArea'
import { WS_APPROVAL_DECISIONS } from '../kodi.constants'

const APPROVE_REPLY = 'Yes, I approve these changes'
const REJECT_REPLY = 'No, I do not approve these changes'

/**
 * Approve / suggest-changes / reject actions for a proposed change, rendered inside the
 * assistant message that proposed it. Approve and reject carry an explicit `approval`
 * decision so the backend can enforce it; "suggest changes" is an ordinary prompt.
 *
 * @param {(message: string, options?: {approval?: string}) => void} props.onRespond -
 *   send the user's reply as a prompt, optionally with an approve/decline decision
 * @param {boolean} props.disabled - disable while a turn is in flight or disconnected
 * @param {() => void} [props.onExpand] - fired when the inline editor opens, so the
 *   container can scroll the now-taller prompt into view
 */
export const KodiApprovalPrompt = ({ onRespond, disabled = false, onExpand }) => {
  const [requestingChanges, setRequestingChanges] = useState(false)
  const [changeText, setChangeText] = useState('')

  const trimmedChange = changeText.trim()

  const openChanges = () => {
    setRequestingChanges(true)
    // Next frame, so the textarea is in the DOM before we scroll it into view.
    requestAnimationFrame(() => onExpand?.())
  }

  const submitChange = () => {
    if (isEmpty(trimmedChange)) {
      return
    }
    onRespond(trimmedChange)
  }

  const handleChangeKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      submitChange()
    }
  }

  const cancelChanges = () => {
    setRequestingChanges(false)
    setChangeText('')
  }

  return (
    <div className="cowork-approval-prompt" data-testid="cowork-approval-prompt">
      {requestingChanges ? (
        <div className="cowork-approval-prompt__changes">
          <label className="cowork-approval-prompt__label" htmlFor="cowork-approval-changes">
            What should change?
          </label>
          <TextArea
            id="cowork-approval-changes"
            className="cowork-approval-prompt__textarea"
            data-testid="cowork-approval-other-input"
            placeholder="Describe what to change…"
            rows={2}
            value={changeText}
            disabled={disabled}
            onChange={(event) => setChangeText(event.target.value)}
            onKeyDown={handleChangeKeyDown}
          />
          <div className="cowork-approval-prompt__actions">
            <Button
              brand
              size="small"
              data-testid="cowork-approval-other-send"
              disabled={disabled || isEmpty(trimmedChange)}
              onClick={submitChange}
            >
              Send
            </Button>
            <Button size="small" data-testid="cowork-approval-cancel" onClick={cancelChanges}>
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <div className="cowork-approval-prompt__bar">
          <div className="cowork-approval-prompt__group">
            <Button
              brand
              size="small"
              icon={<IconCheck aria-hidden="true" />}
              data-testid="cowork-approval-approve"
              disabled={disabled}
              onClick={() => onRespond(APPROVE_REPLY, { approval: WS_APPROVAL_DECISIONS.APPROVE })}
            >
              Approve plan
            </Button>
            <Button
              size="small"
              icon={<IconPencil aria-hidden="true" />}
              data-testid="cowork-approval-other"
              disabled={disabled}
              onClick={openChanges}
            >
              Suggest changes
            </Button>
          </div>
          <Button
            basic
            size="small"
            data-testid="cowork-approval-decline"
            disabled={disabled}
            onClick={() => onRespond(REJECT_REPLY, { approval: WS_APPROVAL_DECISIONS.DECLINE })}
          >
            Reject plan
          </Button>
        </div>
      )}
    </div>
  )
}
